import { RefreshCw, RotateCw } from "lucide-react";
import { useState } from "react";
import { fieldLabelFor } from "@/components/shared/activity-log";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { StatusPill, type StatusTone } from "@/components/shared/status-badge";
import { TabBar } from "@/components/shared/tab-bar";
import { Button } from "@/components/ui/button";
import {
  NOS_PUSH_PAGE_SIZE,
  type NosPushPageRequest,
  type NosPushRow,
  useNosHistoryPushes,
  useNosPushes,
  useRetryNosHistoryPush,
  useRetryNosPush,
} from "@/hooks/use-nos-pushes";
import { formatExact, formatWhen } from "@/lib/dates";
import type { NosPushStatus, NosPushView } from "@/schemas/nos-push";

const STATUS_LABELS: Record<NosPushStatus, string> = {
  pending: "Pending",
  sending: "Sending",
  sent: "Sent",
  skipped: "Skipped",
  superseded: "Superseded",
  failed: "Failed",
};

const STATUS_TONE: Record<NosPushStatus, StatusTone> = {
  pending: "info",
  sending: "info",
  sent: "success",
  skipped: "neutral",
  superseded: "neutral",
  failed: "danger",
};

const VIEW_TABS = [
  { key: "open", label: "Open" },
  { key: "skipped", label: "Skipped" },
  { key: "sent", label: "Sent" },
];

const SOURCE_TABS = [
  { key: "persons", label: "Person edits" },
  { key: "history", label: "Person history" },
];

/**
 * Settings → NOS sync. Two outboxes, two questions: "did this person's edit
 * reach NOS?" and "is NOS's history right?" (manager and role classification,
 * labelled per row).
 *
 * They are separate views rather than one merged list because the rows carry
 * different things — a field diff versus a date span — and because a stuck
 * history push is the more consequential of the two: NOS keeps a wrong span
 * silently, where a stuck person edit just leaves NOS stale.
 */
export function NosSyncTab() {
  const [source, setSource] = useState<"persons" | "history">("persons");
  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-semibold text-foreground">NOS sync</h2>
        <p className="text-sm text-muted-foreground">
          Changes queued to reach NOS 1.0. Enable is authoritative — an edit here always saves even
          when NOS can't be reached — so this is where a stuck delivery becomes visible and
          retryable.
        </p>
      </div>
      <TabBar
        tabs={SOURCE_TABS}
        value={source}
        onChange={(key) => setSource(key === "history" ? "history" : "persons")}
      />
      {source === "persons" ? <PersonPushTable /> : <HistoryPushTable />}
    </div>
  );
}

/**
 * Person edits waiting to reach (or that failed to
 * reach) NOS 1.0 through the outbox (see api/nos-push.ts, lib/nos/push-worker.ts).
 *
 * Three tabs. Open (pending + failed) is the default and the only one that
 * needs attention — Skipped (the person was unlinked from NOS by the time the
 * worker got to it) and Sent are expected outcomes, not problems, so they're
 * kept out of the default view. `superseded` — the ordinary result of editing
 * someone twice — never appears anywhere here.
 */
function PersonPushTable() {
  const [view, setView] = useState<NosPushView>("open");
  const [page, setPage] = useState(1);
  const [pageRequest, setPageRequest] = useState<NosPushPageRequest>({
    cursor: null,
    direction: "next",
  });

  const { data, isLoading, isError, error, refetch } = useNosPushes({ view }, pageRequest);
  const retry = useRetryNosPush();

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load NOS sync status"}
        onRetry={() => refetch()}
      />
    );
  }

  const rows = data?.data ?? [];

  return (
    <div className="space-y-4">
      <TabBar
        tabs={VIEW_TABS}
        value={view}
        onChange={(key) => {
          setView(key === "skipped" ? "skipped" : key === "sent" ? "sent" : "open");
          setPage(1);
          setPageRequest({ cursor: null, direction: "next" });
        }}
      />

      <DataTable
        headers={[
          { label: "Person" },
          { label: "Status", className: "w-32" },
          { label: "Attempts", className: "w-24 text-right" },
          { label: "Last error" },
          { label: TIME_COLUMN_LABEL[view], className: "w-32" },
          { label: "", className: "w-28 text-right" },
        ]}
        loading={isLoading}
        colSpan={6}
        empty={
          <EmptyState
            icon={RefreshCw}
            title={
              view === "open"
                ? "Nothing stuck"
                : view === "skipped"
                  ? "Nothing skipped"
                  : "Nothing sent yet"
            }
            description={
              view === "open"
                ? "Every queued person edit has either reached NOS or hasn't been tried yet."
                : view === "skipped"
                  ? "Skipped rows are people who were unlinked from NOS by the time their edit was sent — none right now."
                  : "Person edits that reached NOS will show up here."
            }
          />
        }
      >
        {rows.map((row) => (
          <NosPushTableRow
            key={row.id}
            row={row}
            onRetry={() => retry.mutate(row.id)}
            busy={retry.isPending}
          />
        ))}
      </DataTable>

      <CursorPagination
        total={data?.total ?? 0}
        page={page}
        limit={NOS_PUSH_PAGE_SIZE}
        hasNext={data?.hasNext ?? false}
        hasPrev={data?.hasPrev ?? false}
        onNext={() => {
          if (data?.nextCursor) {
            setPage((p) => p + 1);
            setPageRequest({ cursor: data.nextCursor, direction: "next" });
          }
        }}
        onPrev={() => {
          if (data?.prevCursor) {
            setPage((p) => Math.max(1, p - 1));
            setPageRequest({ cursor: data.prevCursor, direction: "prev" });
          }
        }}
        loading={isLoading}
        itemLabel="pushes"
      />
    </div>
  );
}

/** How many changed fields to list before collapsing the rest into a count. */
const MAX_LISTED_CHANGES = 3;

/**
 * The time column means something different per tab. An open push is still in
 * line, so when it was queued is the useful fact; a finished one is more
 * usefully dated by when it finished. Labelling every tab "Queued" made a sent
 * push look like it was still waiting.
 */
const TIME_COLUMN_LABEL: Record<NosPushView, string> = {
  open: "Queued",
  sent: "Sent",
  skipped: "Skipped",
};

/**
 * When this push last moved. `sent_at` is stamped on both the sent and the
 * skipped path (skipped being when we stopped trying), so a terminal row has
 * one; `failed` rows do not, and fall back to their queue time — which reads
 * correctly, since they sit on the Open tab.
 */
function whenFor(row: NosPushRow): string | null {
  if (row.status === "sent" || row.status === "skipped") return row.sentAt ?? row.createdAt;
  return row.createdAt;
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

/**
 * What this push changes, one line per field. Values arrive already resolved to
 * display form (see `queryNosPushes`), so nothing here needs to know that
 * `managerId` is a uuid on disk.
 *
 * This is what changed in Enable — not a comparison against NOS. If an earlier
 * push failed, NOS may be further behind than the pair suggests.
 */
function PushChanges({ changes }: { changes: NosPushRow["changes"] }) {
  // Rows queued before `before_payload` existed have no recorded pre-edit
  // state. Rendering nothing would read as "this push changed nothing", which
  // is a different claim — say plainly that we don't know.
  if (changes.length === 0) {
    return (
      <span className="text-xs italic text-muted-foreground">Changed fields not recorded</span>
    );
  }
  const listed = changes.slice(0, MAX_LISTED_CHANGES);
  const remaining = changes.length - listed.length;

  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
      {listed.map((change) => (
        <li key={change.field} className="max-w-full truncate">
          <span className="text-muted-foreground">{fieldLabelFor(change.field)}: </span>
          <span className="text-muted-foreground/70 line-through">
            {displayValue(change.before)}
          </span>
          <span className="text-muted-foreground/70"> → </span>
          <span className="font-medium">{displayValue(change.after)}</span>
        </li>
      ))}
      {remaining > 0 && (
        <li className="text-muted-foreground">
          +{remaining} more field{remaining === 1 ? "" : "s"}
        </li>
      )}
    </ul>
  );
}

function NosPushTableRow({
  row,
  onRetry,
  busy,
}: {
  row: NosPushRow;
  onRetry: () => void;
  busy: boolean;
}) {
  return (
    <>
      <tr className="align-top">
        <td className="px-4 py-3 font-medium">{row.personName}</td>
        <td className="px-4 py-3">
          <StatusPill tone={STATUS_TONE[row.status]} label={STATUS_LABELS[row.status]} />
        </td>
        <td className="px-4 py-3 text-right tabular-nums">{row.attempts}</td>
        <td className="px-4 py-3 text-xs text-muted-foreground">
          {row.lastError ? (
            <span className="whitespace-pre-wrap break-words">{row.lastError}</span>
          ) : (
            "—"
          )}
        </td>
        <td className="px-4 py-3 text-xs text-muted-foreground" title={formatExact(whenFor(row))}>
          {formatWhen(whenFor(row))}
        </td>
        <td className="px-4 py-3 text-right">
          {row.status === "failed" && (
            <Button variant="outline" size="sm" disabled={busy} onClick={onRetry}>
              <RotateCw className="mr-1.5 size-3.5" />
              Retry
            </Button>
          )}
        </td>
      </tr>
      <tr className="border-b">
        <td colSpan={6} className="px-4 pb-3">
          <PushChanges changes={row.changes} />
        </td>
      </tr>
    </>
  );
}

/**
 * Person-history spans queued for NOS — manager and role classification.
 *
 * The same three views as person edits, because the lifecycle is the same. What
 * differs is the row: a span and an operation, rather than a field diff. A
 * `deactivate` row is worth calling out — it is the only push that removes
 * something from NOS's view of history (by flipping IsActive, never a delete).
 */
function HistoryPushTable() {
  const [view, setView] = useState<NosPushView>("open");
  const [page, setPage] = useState(1);
  const [pageRequest, setPageRequest] = useState<NosPushPageRequest>({
    cursor: null,
    direction: "next",
  });

  const { data, isLoading, isError, error, refetch } = useNosHistoryPushes({ view }, pageRequest);
  const retry = useRetryNosHistoryPush();

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load NOS history sync status"}
        onRetry={() => refetch()}
      />
    );
  }

  const rows = data?.data ?? [];

  return (
    <div className="space-y-4">
      <TabBar
        tabs={VIEW_TABS}
        value={view}
        onChange={(key) => {
          setView(key === "skipped" ? "skipped" : key === "sent" ? "sent" : "open");
          setPage(1);
          setPageRequest({ cursor: null, direction: "next" });
        }}
      />

      <DataTable
        headers={[
          { label: "Person" },
          { label: "History", className: "w-40" },
          { label: "Span" },
          { label: "Change", className: "w-28" },
          { label: "Status", className: "w-32" },
          { label: "Attempts", className: "w-24 text-right" },
          { label: "Last error" },
          { label: TIME_COLUMN_LABEL[view], className: "w-32" },
          { label: "", className: "w-28 text-right" },
        ]}
        loading={isLoading}
        colSpan={9}
        empty={
          <EmptyState
            icon={RefreshCw}
            title={
              view === "open"
                ? "Nothing stuck"
                : view === "skipped"
                  ? "Nothing skipped"
                  : "Nothing sent yet"
            }
            description={
              view === "open"
                ? "Every queued history span has either reached NOS or hasn't been tried yet."
                : view === "skipped"
                  ? "Skipped rows are spans NOS no longer has — none right now."
                  : "History spans that reached NOS will show up here."
            }
          />
        }
      >
        {rows.map((row) => {
          const when =
            row.status === "sent" || row.status === "skipped"
              ? (row.sentAt ?? row.createdAt)
              : row.createdAt;
          return (
            <tr key={row.id} className="border-t">
              <td className="px-4 py-2.5 font-medium">{row.personName ?? "—"}</td>
              <td className="px-4 py-2.5 text-muted-foreground text-sm capitalize">{row.series}</td>
              <td className="px-4 py-2.5 text-muted-foreground text-sm">{row.span}</td>
              <td className="px-4 py-2.5">
                <span className="text-muted-foreground text-xs">
                  {row.operation === "deactivate" ? "Remove" : "Update"}
                </span>
              </td>
              <td className="px-4 py-2.5">
                <StatusPill tone={STATUS_TONE[row.status]} label={STATUS_LABELS[row.status]} />
              </td>
              <td className="px-4 py-2.5 text-right text-muted-foreground">{row.attempts}</td>
              <td className="px-4 py-2.5 text-muted-foreground text-sm">{row.lastError ?? "—"}</td>
              <td className="px-4 py-2.5 text-muted-foreground text-sm" title={formatExact(when)}>
                {formatWhen(when)}
              </td>
              <td className="px-4 py-2.5 text-right">
                {row.status === "failed" && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={retry.isPending}
                    onClick={() => retry.mutate(row.id)}
                  >
                    <RotateCw className="mr-1.5 size-3.5" />
                    Retry
                  </Button>
                )}
              </td>
            </tr>
          );
        })}
      </DataTable>

      <CursorPagination
        total={data?.total ?? 0}
        page={page}
        limit={NOS_PUSH_PAGE_SIZE}
        hasNext={data?.hasNext ?? false}
        hasPrev={data?.hasPrev ?? false}
        onNext={() => {
          if (data?.nextCursor) {
            setPage((p) => p + 1);
            setPageRequest({ cursor: data.nextCursor, direction: "next" });
          }
        }}
        onPrev={() => {
          if (data?.prevCursor) {
            setPage((p) => Math.max(1, p - 1));
            setPageRequest({ cursor: data.prevCursor, direction: "prev" });
          }
        }}
        loading={isLoading}
        itemLabel="spans"
      />
    </div>
  );
}
